import { InfoIcon } from 'lucide-react';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { IndividualStudentReportOverallQuery } from '../../graphQLOperations/__generated__/IndividualStudentReportOverallQuery.graphql';
import { FC, useState } from 'react';
import { getOverallProjection } from '../../graphQLOperations/IndividualStudentReport';
import { format } from 'date-fns';
import { Dialog } from 'primereact/dialog';

interface Props {
  queryReferenceProjection: PreloadedQuery<IndividualStudentReportOverallQuery, Record<string, unknown>>;
  diagnostic?: { id: string; retakeAssigned: boolean } | null;
}

export const IndividualStudentReportCards: FC<Props> = ({ queryReferenceProjection, diagnostic }) => {
  const { IndividualReportOverview } = usePreloadedQuery<IndividualStudentReportOverallQuery>(
    getOverallProjection,
    queryReferenceProjection,
  );
  const [modalMetricsVisible, setModalMetricsVisible] = useState(false);

  const progressOverview: { [key: string]: { title: string; value: number } } = {
    demonstratedSkillsInDiagnostic: {
      title: 'Skills demonstrated on diagnostic',
      value: IndividualReportOverview.demonstratedSkillsInDiagnostic,
    },
    demonstratedSkillsSinceDiagnostic: {
      title: 'Skills gained since diagnostic',
      value: IndividualReportOverview.demonstratedSkillsSinceDiagnostic,
    },
    currentPace: { title: 'Average Learning Pace since diagnostic', value: IndividualReportOverview.currentPace },
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-y-4">
      <h3>Overview</h3>
      <Divider className="tw-mt-0" />
      <div className="tw-flex tw-items-center tw-justify-between">
        <div className="tw-flex tw-items-center">
          <h4>Progress from BOY diagnostic</h4>
          <span className="tw-text-neutral-600 tw-mx-3">|</span>
          {IndividualReportOverview.diagnosticDate !== '0001-01-01T00:00:00.000Z' && (
            <span className="tw-text-neutral-600">
              Diagnostic taken: {format(IndividualReportOverview.diagnosticDate, 'P')}
            </span>
          )}
        </div>
        <div>
          <Button
            onClick={() => {
              setModalMetricsVisible(true);
            }}
            label="About these metrics"
            outlined
            className="tw-border-none"
            id="IndividualStudentAbout"
          >
            <InfoIcon className="tw-ml-3" size={20} />
          </Button>
          <Dialog visible={modalMetricsVisible} onHide={() => setModalMetricsVisible(false)} className="tw-w-[30%]">
            <div className="nc_individual-report-skill-modal">
              <p className="tw-font-semibold">
                These metrics track student growth from the Beginning of Year (BOY) diagnostic:
              </p>
              <p>
                <b>Skills demonstrated on diagnostic:</b> This shows the number of skills the student had already
                mastered when they took the BOY diagnostic. This is their starting point.
              </p>
              <p>
                <b>Skills gained:</b> This is the total number of new skills the student has learned since taking the
                BOY diagnostic. It{"'"}s calculated by subtracting the BOY skills from the current total.
              </p>
              <p>
                <b>Average Learning Pace since diagnostic:</b> This shows the estimated average number of skills the
                student has gained per week since the diagnostic. It{"'"}s calculated by dividing skills gained by weeks
                since diagnostic, giving an average pace of R skills/week.
              </p>
            </div>
          </Dialog>
        </div>
      </div>
      {!diagnostic && (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-border-[2px] tw-border-solid tw-border-[#F1F0EE] tw-rounded-2xl tw-bg-[#F9F8F5]">
          <p className="tw-font-bold tw-text-lg tw-text-[#424242] tw-mb-0">No diagnostic data available.</p>
          <p className="tw-text-base tw-font-normal tw-text-[#424242] tw-mt-0">
            {`This student hasn't taken a Beginning of Year diagnostic yet. Growth metrics will appear here once completed.`}
          </p>
        </div>
      )}
      {diagnostic?.retakeAssigned && (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-border-[2px] tw-border-solid tw-border-[#F1F0EE] tw-rounded-2xl tw-bg-[#F9F8F5]">
          <p className="tw-font-bold tw-text-lg tw-text-[#424242] tw-mb-0">No diagnostic data available.</p>
          <p className="tw-text-base tw-font-normal tw-text-[#424242] tw-mt-0">
            {`This student was assigned a retake diagnostic. Once it's completed, results will appear here.`}
          </p>
        </div>
      )}
      <div className="tw-flex tw-justify-between tw-mt-4">
        {Object.keys(progressOverview).map(item => (
          <div
            key={item}
            className="tw-w-[32%] tw-border-[1px] tw-py-4 tw-px-3 tw-border-solid tw-border-neutral-300 tw-rounded-lg tw-bg-neutral-0"
          >
            <p className="tw-text-center tw-font-normal tw-text-neutral-800">{progressOverview[item].title}</p>
            <h3 className="text-center">{`${
              item === 'demonstratedSkillsSinceDiagnostic' && progressOverview[item].value > 0 ? '+' : ''
            } ${progressOverview[item].value > 0 ? progressOverview[item].value : '0'}`}</h3>
            {item === 'currentPace' && <span className="tw-text-center tw-block tw-text-neutral-600">skills/week</span>}
          </div>
        ))}
      </div>
    </div>
  );
};
